/* Copyright 2016-2020 The MathWorks, Inc. */
define([
    "dijit/focus",
    "dojo/aspect",
    "dojo/_base/declare",
    "dojo/_base/lang",
    "dojo/keys",
    "dojo/query",
    "MW/toolstrip/form/SplitButton",
    "mw-form/Menu",
    "mw-form/mixins/event/ChangeEventMixin",
    "mw-colorpicker/ColorPicker",
    "MW/toolstrip/constants/RecentColorsMode",
    "MW/toolstrip/constants/SwatchColorMode"
], function (focus, aspect, declare, lang, keys, query, SplitButton, Menu, ChangeEventMixin, ColorPicker, RecentColorsMode, SwatchColorMode) {

    return declare([SplitButton, ChangeEventMixin], {

        baseClass: "mwWidget mwSplitButton mwColorPickerButton",
        visualFamily: "default",

        selectedColor: "#ff0000",
        recentColorsMode: RecentColorsMode.DEFAULT,
        closeOnColorSelection: true,
        autoColor: "#000000",
        displayView: "standard",
        showAuto: false,
        showNone: false,
        showFlat: false,
        showInterp: false,
        showTexturemap: false,
        swatchColor: "#000000",
        swatchColorMode: SwatchColorMode.AUTO,

        constructor: function () {

            this._colorPickerMenu = new Menu();
            this._colorPicker = new ColorPicker();
            this._colorPicker.startup();
            this._swatchNode = document.createElement("div");
            this.own(this._colorPickerMenu);
            this.own(this._colorPicker);
            this._setupListeners();
        },

        _setupListeners: function () {
            var watchSelectedColor = this.watch("selectedColor", function (name, oldValue, value) {
                if (this.swatchColorMode === SwatchColorMode.AUTO && oldValue !== value) {
                    this.set("swatchColor", value);
                }
            });
            this.own(watchSelectedColor);
        },

        buildRendering: function () {

            this.inherited(arguments);
            this._colorPickerMenu.addChild(this._colorPicker);
            this._swatchNode.classList.add("mwColorSwatchNode");
            this.iconNode.appendChild(this._swatchNode);
            this.set("menu", this._colorPickerMenu);
        },

        _setSelectedColorAttr: function (color) {

            if (typeof color !== "string")
                throw new Error("'selectedColor' property expects a literal string!");

            this._validateColor(color);
            this._set("selectedColor", color);
            this._updatePreview();
        },

        _setRecentlyUsedColorsAttr: function (value) {
            this._colorPicker.set("recentlyUsedColors", value);
            this._set("recentlyUsedColors", value);
        },

        _getRecentlyUsedColorsAttr: function () {
            return this._colorPicker.get("recentlyUsedColors");
        },

        _setRecentColorsTitleAttr: function (value) {
            this._colorPicker.set("recentColorsTitle", value);
            this._set("recentColorsTitle", value);
        },

        _setRecentColorsModeAttr: function (value) {
            this._set("recentColorsMode", value);
        },

        // Setting the properties locally so that if the mw-colorpicker properties are accessed
        // using widget.propertyName and not using getter method, we can still provide the property values.
        _setAutoColorAttr: function(value) {
            this._colorPicker.set("autoColor", value);
            this._set("autoColor", value);
        },

        _getAutoColorAttr: function() {
            return this._colorPicker.get("autoColor");
        },

        _setDisplayViewAttr: function(value) {
            this._colorPicker.set("displayView", value);
            this._set("displayView", value);
        },

        _getDisplayViewAttr: function() {
            return this._colorPicker.get("displayView");
        },

        _setShowAutoAttr: function(value) {
            this._colorPicker.set("showAuto", value);
            this._set("showAuto", value);
        },

        _getShowAutoAttr: function() {
            return this._colorPicker.get("showAuto");
        },

        _setShowNoneAttr: function(value) {
            this._colorPicker.set("showNone", value);
            this._set("showNone", value);
        },

        _getShowNoneAttr: function() {
            return this._colorPicker.get("showNone");
        },

        _setShowFlatAttr: function(value) {
            this._colorPicker.set("showFlat", value);
            this._set("showFlat", value);
        },

        _getShowFlatAttr: function() {
            return this._colorPicker.get("showFlat");
        },

        _setShowInterpAttr: function(value) {
            this._colorPicker.set("showInterp", value);
            this._set("showInterp", value);
        },

        _getShowInterpAttr: function() {
            return this._colorPicker.get("showInterp");
        },

        _setShowTexturemapAttr: function(value) {
            this._colorPicker.set("showTexturemap", value);
            this._set("showTexturemap", value);
        },

        _getShowTexturemapAttr: function() {
            return this._colorPicker.get("showTexturemap");
        },

        _setSwatchColorAttr: function (color) {
            if (typeof color !== "string") {
                throw new Error("'swatchColor' property expects a literal string!");
            }

            this._validateColor(color);

            var newSwatchColor;
            if (this.swatchColorMode === SwatchColorMode.AUTO) {
                var selectedColor = this.get("selectedColor");
                newSwatchColor = selectedColor === 'auto' ? this.get("autoColor"): selectedColor;
            } else {
                newSwatchColor = color === "auto" ? this.get("autoColor") : color;
            }
            this._swatchNode.style.backgroundColor = newSwatchColor;
            this.swatchColor = newSwatchColor;
        },

        _setSwatchColorModeAttr: function (swatchColorMode) {
            if (typeof swatchColorMode !== "string") {
                throw new Error("'swatchColorMode' property expects a literal string!");
            }

            var isValidMode = Object.values(SwatchColorMode).some(function (mode) {
                return mode === swatchColorMode;
            });
            if (!isValidMode) {
                throw new Error("'swatchColorMode' property can be either 'auto' or 'manual'!");
            }
            this.swatchColorMode = swatchColorMode;
        },

        postCreate: function () {

            this.inherited(arguments);

            /**
             * Using aspect.after to update the selectedColor after a standard/ recent color palette node is clicked. This is needed because
             * the PaletteMixin stops the cell click event from propagating up. Need to listen on click events because
             * we cannot use the change events to diferentiate if the event occured on selecting a color swatch or on entering custom colors.
             */
            this.own(aspect.after(this._colorPicker.paletteNode, "_onCellClick", lang.hitch(this, "_updateSelectedColor")));
            this.own(aspect.after(this._colorPicker.recentColorsWidgetNode, "_onCellClick", lang.hitch(this, "_updateSelectedColor")));

            this.menu.domNode.addEventListener("close", this._handleMenuClose.bind(this));

            //To manage focus on up/down and tab keys
            this.menu.domNode.addEventListener("keydown", this._handleMenuKeyDown.bind(this));

            //To track the keyboard events when entering custom color values
            this._colorPicker.domNode.addEventListener("keydown", this._handleColorPickerKeyDown.bind(this));

            //To fire event when button is clicked
            this.buttonNode.addEventListener("click", this._handleButtonClick.bind(this));
        },

        /**
         * This function is responsible for firing a change event when the button node is clicked.
         * @private
         */
        _handleButtonClick: function () {
            var propertyName = this.swatchColorMode === SwatchColorMode.AUTO ? "selectedColor" : "swatchColor";
            var color = this.get(propertyName);
            this._triggerChangeEvent(propertyName, color, color);
        },

        /**
         * This function is responsible to handle closing of the menu.
         * @private
         */
        _handleMenuClose: function () {
            //g2222756: setTimeout to allow ColorPicker to update itself
            setTimeout(function () {
                // g1772666: Closing of the menu due to blur is equivalent to a commit and should update the
                // selected color, recent colors list and the swatchNode background color. But we should check
                // if the menu is not closed due to escape key, in which case, it is equivalent to cancel and the
                // color value is ignored. Also, if the Enter key is pressed on the spinner, then there is no need to
                // update the selected color again as it is already handled in _handleColorPickerKeyDown function.
                if (!this._escapeKeyPressed && !this._enterKeyPressedOnSpinner) {
                        this._updateSelectedColor(true);
                } else {
                    this._escapeKeyPressed = false;
                    this._enterKeyPressedOnSpinner = false;
                    //to reset preview to the last selected color before closing the menu
                    this._updatePreview();
                }
            }.bind(this), 0);
        },

        /**
         * This function helps to focus the colorPicker palette Node on up/down arrow keys
         * when the target is menu. Stopping the event propagation on TAB key so that menu does not close. Menu explicitly
         * listens to tab key events and fires blur events that cause it close.
         * @param event
         * @private
         */
        _handleMenuKeyDown: function (event) {
            /*
             Focus paletteNode on up/down arrow when target node is menu.
             To prevent the paletteNode from getting focus on up/down arrow when focus
             is currently in the custom picker region
             */
            if (event.keyCode === keys.DOWN_ARROW || event.keyCode === keys.UP_ARROW) {
                this._colorPicker.paletteNode.focus();

            } else if (event.keyCode === keys.TAB) {
                //to prevent menu from closing on hitting the tab key
                event.stopPropagation();
            } else if (event.keyCode === keys.ESCAPE) {
                this._escapeKeyPressed = true;
            }
        },

        /**
         * This function checks the closeOnColorSelection property.
         * The menu is closed after color selection only if the property evaluates to true.
         * @private
         */
        _closeMenuOnColorSelection: function () {

            if (this.closeOnColorSelection) {
                this.closeMenu();
            }
        },

        /**
         * This function handles the key down events like ENTER and ESCAPE on the Custom Picker region in colorPicker.
         * On ENTER: the custom color value entered is committed. The preview, swatch node and recent colors are updated
         * On ESCAPE: the custom color value entered is ignored and the preview is reset to last selected color
         * @param event
         * @private
         */
        _handleColorPickerKeyDown: function (event) {

            if (event.target.tagName === "INPUT") {
                if (event.keyCode === keys.ENTER) {
                    this._enterKeyPressedOnSpinner = true;

                    // Need timeout to wait for the change event from mw-colorpicker to be emitted.
                    // Once the change event has been emitted and value property of the mw-colorpicker has been set,
                    // we can update the selected color and the color swatch.
                    setTimeout(function() {
                        this._updateSelectedColor();
                    }.bind(this), 100);

                } else if (event.keyCode === keys.ESCAPE) {
                    //to reset preview to last selected color
                    this.set("selectedColor", this.get("selectedColor"));
                }
            }
        },

        _updatePreview: function () {
            //To update colorpicker preview if it does not match with the selected color
            if (this.get("selectedColor") !== this._colorPicker.value) {
                this._colorPicker.set("value", this.get("selectedColor"), false);
            }
        },

        /**
         * This function updates the Recent Colors list by checking if the selected color exists in the Recent Colors list.
         * If it already exists, then the particular color is removed from the old index location and pushed into 0 index location.
         * Else, it is added to 0 index location straightaway.
         * @private
         */
        _updateRecentColors: function () {

            var color = this.get("selectedColor");

            var recentlyUsedColors = this._colorPicker.get("recentlyUsedColors");
            var colorIndex = recentlyUsedColors.indexOf(color);
            // If selected color already exists in the recent colors list, remove it from its current position in the list
            // so that it can be placed at the 0th index position. A max of 7 colors are allowed in the recent colors list.
            if (colorIndex !== -1 && colorIndex < recentlyUsedColors.length) {
                recentlyUsedColors.splice(colorIndex, 1);
            } else if (recentlyUsedColors.length >= 7 ) {
                // If a new color is being added to recent colors list, then pop the last color in list
                recentlyUsedColors.pop();
            }
            recentlyUsedColors.unshift(color);
            this._colorPicker.set("recentlyUsedColors", recentlyUsedColors);

            //aspect added on recent colors node again because it was lost after destruction of recentColors domNode within recentlyUsedColors setter in colorpicker
            this.own(aspect.after(this._colorPicker.recentColorsWidgetNode, "_onCellClick", lang.hitch(this, "_updateSelectedColor")));
        },

        /**
         * This function updates the selectedColor, recent colors list and handles the closing of the menu based on
         * closeOnColorSelection property
         * @private
         */
        _updateSelectedColor: function (menuClosed) {

            var oldValue = this.get("selectedColor");
            var newValue = this._colorPicker.get("value");

            // g1902168: Change event should not be fired if the selectedColor is not changed.
            if (oldValue !== newValue) {
                this.set("selectedColor", newValue);
                if (this.recentColorsMode === RecentColorsMode.DEFAULT) {
                    this._updateRecentColors();
                }

                this._triggerChangeEvent("selectedColor", oldValue, newValue);
            } else if (!menuClosed) { // g1934549 - Fire a change event when the same color is selected from the
                // menu. Note that the event is not fired when the menu is just opened and closed without selecting
                // any color.
                this._triggerChangeEvent("selectedColor", oldValue, newValue);
            }
            this._closeMenuOnColorSelection();
        },

        /**
         * This function validates the input color value before setting it as the selectedColor property value.
         * @param color : input color to be set as selectedColor property
         * @returns {boolean}
         * @private
         */
        _validateColor: function (color) {

            var colorType = this._isRGBOrHex(color);

            if (colorType === "invalid") {
                throw new Error("'color' invalid value!");

            } else if (colorType === "auto") {

                /* No-op, as 'auto' is valid.  Simply placing condition to indicate that this case was taken care of */

            } else if (colorType === "hex") {

                var hexPattern = /[0-9a-f]{6}/i;
                var isValidHexValue = hexPattern.test(color);

                if (!isValidHexValue) {
                    throw new Error("'color' invalid value!");
                }

            } else if (colorType === "rgb") {

                var isDecimal;
                var colorLength = color.length;
                color = color.substring(4, colorLength - 1);
                var rgbColors = color.split(",");

                //throw error if all R,G,B values not present
                if (rgbColors.length !== 3) {
                    throw new Error("'color' invalid value!");
                }

                //Check if decimal values present
                rgbColors.forEach(function (arg) {
                    arg = parseFloat(arg);
                    if (arg % 1 !== 0) {
                        isDecimal = true;
                    }
                });

                //If decimal values present, validate for rgb double values
                if (isDecimal) {
                    if (this._checkRGBDouble(rgbColors)) {
                        throw new Error("'color' invalid value!");
                    }
                } else {
                    //If no decimal values present, validate for rgb int values
                    if (this._checkRGBInt(rgbColors)) {
                        throw new Error("''color' invalid value!");
                    }
                }
            }
        },

        /**
         * This function determines if the input color if hex, rgb or invalid type
         * @param color : input color to be set as selectedColor property
         * @returns {*}
         * @private
         */
        _isRGBOrHex: function (color) {
            if (color.indexOf("#") === 0) {
                return "hex";
            } else if (color.indexOf("rgb") === 0) {
                return "rgb";
            } else if (color.indexOf("auto") === 0) {
                return "auto";
            } else {
                return "invalid";
            }

        },

        /**
         * This function checks if any of the rgb double values are invalid
         * @param rgbColors : R,G,B values
         * @returns {boolean}
         * @private
         */
        _checkRGBDouble: function (rgbColors) {

            for (var i = 0; i < 3; i++) {
                var arg = parseFloat(rgbColors[i]);
                if (arg < 0 || arg > 1) {
                    return true;
                }
            }
        },

        /**
         * This function checks if any of the rgb int values are invalid
         * @param rgbColors : R,G,B values
         * @returns {boolean}
         * @private
         */
        _checkRGBInt: function (rgbColors) {

            for (var i = 0; i < 3; i++) {
                var arg = parseInt(rgbColors[i]);
                if (arg < 0 || arg > 255) {
                    return true;
                }
            }
        },


        /** Following are QE utility methods for callback testing */
        _qeUpdateSelectedColor: function() {
            this._updateSelectedColor();

        }

    });

});
